import { Menu, RadioGroup } from "@headlessui/react"
import ChevronDownIcon from "@heroicons/react/solid/ChevronDownIcon"
import GenericSingleSelector from "components/DropdownSelectors/GenericSingleSelector"
import ImageFileUploader from "components/FileUploader/Image"
import UrlPreviewComponent from "components/FileUploader/URLPreviewComponent"
import Translation from "components/Translation"
import { useTenantContext } from "context/TenantContext"
import { useState } from "react"
import { LINK_REGEX } from "types/common"
import { IntakeAnswer, IntakeQuestion, QuestionDisplayType } from "types/intake-forms"
import { classNames } from "utils/dom-helpers"

type IntakeQuestionCardProps = {
    intake_question: IntakeQuestion
    ordinal: number
    answers: IntakeAnswer[]
    setAnswers(answers: IntakeAnswer[]): void
    disabled?: boolean
}

export default function IntakeQuestionCard(props: IntakeQuestionCardProps) {
    const {
        intake_question,
        ordinal,
        answers,
        setAnswers,
        disabled
    } = props
    const { primaryColor, tenant } = useTenantContext()
    const [show_upload_view, toggleShowUploadView] = useState<boolean>(false)

    function renderAnswerField() {
        switch (intake_question.display_type) {
            case QuestionDisplayType.TEXT:
                return (
                    <input
                        type="text"
                        className="flex w-full bg-white border border-neutral-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        value={answers?.[0]?.answer || ""}
                        onChange={(e) => {
                            setAnswers([{ answer: e.target.value, answer_number: 0 }])
                        }}
                        disabled={disabled}
                    />
                )
            case QuestionDisplayType.YES_NO:
            case QuestionDisplayType.RADIO_BUTTON:
                return (
                    <RadioGroup
                        value={answers?.[0] || ""}
                        onChange={(v: IntakeAnswer) => {
                            setAnswers([v])
                        }}
                        disabled={disabled}
                    >
                        <div className={classNames("flex gap-x-8 gap-y-2 text-xs font-[450]", intake_question.display_type == QuestionDisplayType.YES_NO ? "flex-row" : "flex-col")}>
                            {intake_question.answers?.map((answer) => {
                                return (
                                    <RadioGroup.Option key={answer.id} value={answer}>
                                        {({ checked }) => (
                                            <div className={classNames("flex items-center gap-2 text-base", disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer")}>
                                                <div
                                                    className='rounded-full w-6 h-6 border-2 flex items-center'
                                                    style={{
                                                        borderColor: checked ? primaryColor : "#BCBEC0"
                                                    }}
                                                >
                                                    <i
                                                        className='rounded-full w-4 h-4 block m-auto'
                                                        style={{
                                                            backgroundColor: checked ? primaryColor : ""
                                                        }}
                                                    />
                                                </div>
                                                <span
                                                    style={{
                                                        color: checked ? primaryColor : "#58595B"
                                                    }}
                                                >
                                                    {answer.answer}
                                                </span>
                                            </div>
                                        )}
                                    </RadioGroup.Option>
                                )
                            })}
                        </div>
                    </RadioGroup>
                )
            case QuestionDisplayType.DROPDOWN:
                return (
                    <GenericSingleSelector
                        class_name="min-w-[40%] w-full sm:w-fit"
                        disable={disabled}
                        options_class_name='max-h-60 overflow-y-auto'
                        items={intake_question.answers || []}
                        selected_item={answers?.[0] || ""}
                        titleDisplay={(item: IntakeAnswer) => {
                            return (
                                <>
                                    <span className={classNames('flex flex-grow font-[450]', item.answer ? "" : "text-gray-two")}>{item.answer ? item.answer : <Translation content_key="booking.select" />}</span>
                                    <ChevronDownIcon
                                        className="flex -mr-1 ml-2 h-5 w-5 text-primary-light"
                                        aria-hidden="true"
                                    />
                                </>
                            )
                        }}
                        itemDisplay={(item: IntakeAnswer, index: number, is_selected: boolean) => {
                            return (
                                <Menu.Item key={item.id}>
                                    {({ active }) => (
                                        <div
                                            className={classNames(
                                                active
                                                    ? 'bg-[#86C5E0]/10'
                                                    : 'bg-white',
                                                'flex flex-col text-xs font-medium font-[#58595B] p-2 rounded-lg whitespace-normal',
                                            )}
                                            onClick={() => {
                                                setAnswers([item])
                                            }}
                                        >
                                            {item.answer}
                                        </div>
                                    )}
                                </Menu.Item>
                            )
                        }}
                        isItemSelected={(item: IntakeAnswer, selected_item: IntakeAnswer) => {
                            return item.id === selected_item.id
                        }}
                    />
                )
            case QuestionDisplayType.CHECKBOX:
                return (
                    <div className="flex flex-col w-full gap-y-2">
                        {intake_question.answers && intake_question.answers.map((option) => {
                            const index = answers ? answers?.findIndex((answer) => answer.id == option.id) : -1
                            return (
                                <div key={option.id} className='flex flex-row'>
                                    <input
                                        type='checkbox'
                                        className="h-5 w-5 border-gray-300 rounded text-primary focus:ring-primary-light checked:bg-primary-light mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        checked={index > -1}
                                        onChange={() => {
                                            let new_answers = answers ? answers.slice() : []
                                            if (index > -1) {
                                                new_answers.splice(index, 1)
                                            } else {
                                                new_answers.push(option)
                                            }
                                            setAnswers(new_answers)
                                        }}
                                        disabled={disabled}
                                    />
                                    {option.answer}
                                </div>
                            )
                        })}
                    </div>
                )
            case QuestionDisplayType.FILE:
                return (
                    <>
                        {answers?.[0]?.answer.length > 0 && !show_upload_view ?

                            <UrlPreviewComponent
                                image_url={answers?.[0]?.answer}
                                image_alt='upload image'
                                toggleShowUploadView={toggleShowUploadView}
                                replace_text="booking.replace_image"
                            />
                            :
                            <ImageFileUploader
                                tenant_id={tenant?.id}
                                accept=".jpeg, .jpg, .gif, .png"
                                addClassNames={{
                                    dropzone:
                                        'bg-transparent p-0 overflow-hidden h-64 border-0',
                                    inputLabel:
                                        'text-gray-400 font-medium text-lg flex-grow',
                                    submitButtonContainer: 'mt-3',
                                    submitButton:
                                        'flex gap-3 items-center bg-primary-light text-white px-6 py-3 rounded-lg w-full text-lg font-serif font-light justify-center',
                                }}
                                onAdd={(image_url: string) => {
                                    setAnswers([{ answer: image_url, answer_number: 0 }])
                                }}
                            />
                        }
                    </>

                )
        }
    }

    function parseQuestion(): string { //styling is set because tailwind overrides it by default
        return intake_question.question.replace(LINK_REGEX, function (link: string) {
            return '<a class="underline text-blue-500" target="_blank" href="' + link + '">' + link + '</a>'
        })
    }

    return (
        <div id={intake_question.id} className="flex flex-col justify-center w-full bg-white border border-gray-four rounded px-4 py-5 mb-4 gap-y-4">
            <div className="flex flex-row items-center w-full text-black-one text-sm font-[450]">
                {ordinal}.&nbsp;<h3 dangerouslySetInnerHTML={{ __html: parseQuestion() }} />
                {intake_question.required &&
                    <span className="text-red-500">
                        *
                    </span>}
            </div>
            <div className="flex w-full h-fit">
                {renderAnswerField()}
            </div>
        </div>
    )
}